import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { message } from "antd";
import i18n from "i18next";

const db = firebase.firestore();
const storage = firebase.storage();

const collectionName = "productColorShades";

/**
 * Hook to fetch and manage product color shades from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {boolean} options.activeOnly - Fetch only active shades (default: false)
 * @returns {Object} - Color shades data and utility functions
 */
export const useProductColorShades = (options = {}) => {
  const { autoFetch = true, realtime = true, activeOnly = false } = options;
  const auth = useSelector((state) => state.firebaseReducer.auth);

  const [colorShades, setColorShades] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all product color shades
   * @returns {Promise<Array>} - Array of color shade documents
   */
  const fetchColorShades = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let query = db.collection(collectionName).orderBy("sortOrder", "asc");

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const snapshot = await query.get();

      if (snapshot.empty) {
        setColorShades([]);
        return [];
      }

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setColorShades(data);
      return data;
    } catch (err) {
      console.error("Error fetching product color shades:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [activeOnly]);

  /**
   * Add a new product color shade
   * @param {Object} shadeData - Shade data to add
   * @param {string} shadeData.name - Shade name
   * @param {string} shadeData.code - Shade code
   * @param {number} shadeData.sortOrder - Sort order (optional)
   * @returns {Promise<Object>} - Created shade document
   */
  const addColorShade = useCallback(
    async (shadeData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        let imageUrl = null;

        // Upload image to Firebase Storage if file is provided
        if (shadeData.imageFile) {
          const file = shadeData.imageFile;
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = storage.ref(`product-color-shades/${fileName}`);

          await storageRef.put(file);
          imageUrl = await storageRef.getDownloadURL();
        }

        const newShade = {
          name: shadeData.name,
          nameEN: shadeData.nameEN,
          nameDE: shadeData.nameDE,
          code: shadeData.code,
          image: imageUrl,
          sortOrder: shadeData.sortOrder !== undefined ? shadeData.sortOrder : 999,
          active: shadeData.active !== undefined ? shadeData.active : true,
          createdAt: dayjs().unix(),
          createdBy: auth.email,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        const docRef = await db.collection(collectionName).add(newShade);
        const createdShade = { id: docRef.id, ...newShade };

        message.success(i18n.t("notifications.color_shade_added"));
        return createdShade;
      } catch (err) {
        console.error("Error adding product color shade:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Update an existing product color shade
   * @param {string} shadeId - Shade document ID
   * @param {Object} shadeData - Updated shade data
   * @returns {Promise<Object>} - Updated shade document
   */
  const updateColorShade = useCallback(
    async (shadeId, shadeData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const shadeRef = db.collection(collectionName).doc(shadeId);
        const shadeDoc = await shadeRef.get();

        if (!shadeDoc.exists) {
          throw new Error("Color shade not found");
        }

        const existingData = shadeDoc.data();
        let imageUrl = shadeData.image !== undefined ? shadeData.image : existingData.image;

        // Upload new image if file is provided
        if (shadeData.imageFile) {
          // Delete old image if exists
          if (existingData.image && existingData.image.includes("firebase")) {
            try {
              const oldImageRef = storage.refFromURL(existingData.image);
              await oldImageRef.delete();
            } catch (err) {
              console.warn("Could not delete old image:", err);
            }
          }

          // Upload new image
          const file = shadeData.imageFile;
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = storage.ref(`product-color-shades/${fileName}`);

          await storageRef.put(file);
          imageUrl = await storageRef.getDownloadURL();
        }

        const updatedData = {
          name: shadeData.name !== undefined ? shadeData.name : existingData.name,
          nameEN: shadeData.nameEN !== undefined ? shadeData.nameEN : existingData.nameEN,
          nameDE: shadeData.nameDE !== undefined ? shadeData.nameDE : existingData.nameDE,
          code: shadeData.code !== undefined ? shadeData.code : existingData.code,
          image: imageUrl,
          sortOrder: shadeData.sortOrder !== undefined ? shadeData.sortOrder : existingData.sortOrder,
          active: shadeData.active !== undefined ? shadeData.active : existingData.active,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        await shadeRef.update(updatedData);

        const updatedShade = { id: shadeId, ...existingData, ...updatedData };
        message.success(i18n.t("notifications.color_shade_updated"));
        return updatedShade;
      } catch (err) {
        console.error("Error updating product color shade:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Delete a product color shade
   * @param {string} shadeId - Shade document ID
   * @returns {Promise<void>}
   */
  const deleteColorShade = useCallback(
    async (shadeId) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const shadeRef = db.collection(collectionName).doc(shadeId);
        const shadeDoc = await shadeRef.get();

        if (!shadeDoc.exists) {
          throw new Error("Color shade not found");
        }

        await shadeRef.delete();
        message.success(i18n.t("notifications.color_shade_deleted"));
      } catch (err) {
        console.error("Error deleting product color shade:", err);
        setError(err);
        message.error(i18n.t("notifications.delete_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  // Set up real-time listener or fetch once
  useEffect(() => {
    if (!autoFetch) return;

    if (auth.isEmpty) {
      setColorShades([]);
      return;
    }

    if (realtime) {
      setIsLoading(true);
      let query = db.collection(collectionName).orderBy("sortOrder", "asc");

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setColorShades(data);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error("useProductColorShades: Error in listener:", err);
          setError(err);
          setColorShades([]);
          setIsLoading(false);
        },
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      fetchColorShades();
    }
  }, [autoFetch, realtime, activeOnly, fetchColorShades, auth.isEmpty]);

  return {
    colorShades,
    isLoading,
    error,
    fetchColorShades,
    addColorShade,
    updateColorShade,
    deleteColorShade,
  };
};
